import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Sports } from '@gin-betting/constants';
import { App } from '@gin-betting/core';
import cn from 'classnames';
import { partition, startCase } from 'lodash';

import { getMapNameFromMeta, getScoreTitle } from 'utils';
import usePromoMatchDynamicScoreList from './usePromoMatchDynamicScoreList';

import './promoMatchDynamicScoreList.scss';

type Props = {
    matchId: string;
    sportId: string;
    showDynamicScores: boolean;
};

const PromoMatchDynamicScoreList: React.FC<Props> = ({
    matchId,
    sportId,
    showDynamicScores,
}) => {
    const {
        scrollBarsRef,
        isArrowRotated,
        server,
        scores,
        onArrowClick,
        onScrollUpdate,
        currentMatchPart,
        meta,
    } = usePromoMatchDynamicScoreList({ matchId });

    const [[gamePoints], scoreWithoutGamePoint] =
        sportId === Sports.TENNIS
            ? partition(scores, s => s.type === App.ScoreTypes.TYPE_GAME_POINTS)
            : [[], scores];

    const [[totalScore], scoresWithoutTotal] = partition(
        scoreWithoutGamePoint,
        s => s.type === App.ScoreTypes.TYPE_TOTAL
    );
    const homeTotalScore = totalScore?.home ?? '';
    const awayTotalScore = totalScore?.away ?? '';

    const TennisPoints =
        showDynamicScores && gamePoints ? (
            <div styleName="score">
                <div styleName="count">{gamePoints.home}</div>
                <div styleName="score-title">
                    {getScoreTitle({ meta, score: gamePoints })}
                </div>
                <div styleName="count">{gamePoints.away}</div>
            </div>
        ) : null;

    return (
        <div
            className="__app-PromoMatchBody-score-container"
            styleName="score-container"
        >
            {TennisPoints}
            <div
                styleName={cn('total-score', {
                    'small-font':
                        homeTotalScore.length + awayTotalScore.length > 4,
                })}
            >
                {showDynamicScores && (
                    <div
                        styleName={cn('server home', {
                            active: server === 'home',
                        })}
                    />
                )}
                <div styleName="score-point home">{homeTotalScore}</div>
                <div
                    styleName="score-divider"
                    className="__app-PromoMatchBody-score-divider"
                >
                    :
                </div>

                <div styleName="score-point away">{awayTotalScore}</div>

                {showDynamicScores && (
                    <div
                        styleName={cn('server away', {
                            active: server === 'away',
                        })}
                    />
                )}
            </div>
            {showDynamicScores && (
                <div
                    styleName={cn('dynamic-scores', {
                        'with-borders': scoresWithoutTotal.length !== 0,
                    })}
                >
                    <Scrollbars
                        ref={scrollBarsRef}
                        autoHide={true}
                        hideTracksWhenNotNeeded={true}
                        autoHeight={true}
                        autoHeightMin={16}
                        autoHeightMax={56}
                        onUpdate={onScrollUpdate}
                    >
                        {scoresWithoutTotal
                            .slice()
                            .reverse()
                            .map(({ type, indexNumber, home, away }) => (
                                <div key={type + indexNumber} styleName="score">
                                    <div styleName="count">{home}</div>
                                    <div
                                        styleName="score-title"
                                        title={startCase(
                                            `${type} ${indexNumber}`
                                        )}
                                    >
                                        {getMapNameFromMeta({
                                            format: 'short',
                                            map: getScoreTitle({
                                                meta,
                                                score: {
                                                    type,
                                                    indexNumber,
                                                },
                                                currentMatchPart,
                                            }),
                                        })}
                                    </div>
                                    <div styleName="count">{away}</div>
                                </div>
                            ))}
                    </Scrollbars>

                    {scoresWithoutTotal.length > 2 && (
                        <div
                            styleName={cn('arrow-down', {
                                up: isArrowRotated,
                            })}
                            onClick={onArrowClick}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default PromoMatchDynamicScoreList;
